import React, { useState } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddFormDesign = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    form_type: '',
    fields: [{ label: '', name: '', type: 'text', required: false }]
  });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'tel', label: 'Phone' },
    { value: 'date', label: 'Date' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Dropdown' },
    { value: 'radio', label: 'Radio' },
    { value: 'checkbox', label: 'Checkbox' },
  ];

  const formTypes = [
    { value: 'enquiry', label: 'Enquiry' },
    { value: 'admission', label: 'Admission' },
    { value: 'registration', label: 'Registration' },
  ];

  const handleFormTypeChange = (e) => {
    setFormData({ ...formData, form_type: e.target.value });
  };

  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...formData.fields];
    updatedFields[index][field] = value;
    setFormData({ ...formData, fields: updatedFields });
  };

  const addField = () => {
    setFormData({
      ...formData,
      fields: [...formData.fields, { label: '', name: '', type: 'text', required: false }]
    });
  };

  const removeField = (index) => {
    const updatedFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: updatedFields });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.form_type) {
      setSnackbarMessage('Please select a form type');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    if (formData.fields.length === 0) {
      setSnackbarMessage('Please add at least one field');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    const invalidFields = formData.fields.filter(
      field => !field.label || !field.name || !field.type
    );

    if (invalidFields.length > 0) {
      setSnackbarMessage('Please fill all field details');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      
      const payload = {
        form_type: formData.form_type,
        layout_json: {
          fields: formData.fields
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/master/form-designs/`,
        payload,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );

      setSnackbarMessage('Form design created successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      setTimeout(() => navigate('/form-design'), 1000);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || 'Failed to create form design');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/form-design');

  const isFormValid = () => {
    return formData.form_type && 
           formData.fields.length > 0 && 
           formData.fields.every(field => field.label && field.name && field.type);
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        component={Paper}
        elevation={3}
        sx={{ p: 4, borderRadius: 2, backgroundColor: theme.palette.background.paper }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Add New Form Design
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
          <FormControl fullWidth required>
  <InputLabel
    shrink
    sx={{
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Label color
      '&.Mui-focused': {
        color: theme.palette.primary.main, // Focused label color
      },
    }}
  >
    Form Type *
  </InputLabel>
  <Select
    name="form_type"
    value={formData.form_type}
    onChange={handleFormTypeChange}
    label="Form Type *"
    displayEmpty
    sx={{
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Selected value color
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider, // Border color
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main, // Hover border color
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main, // Focused border color
      },
      '& .MuiSelect-icon': {
        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Dropdown icon color
      },
    }}
  >
    <MenuItem value="" disabled>
      Select Form Type
    </MenuItem>
    {formTypes.map((type) => (
      <MenuItem
        key={type.value}
        value={type.value}
        sx={{
          color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Dropdown item color
        }}
      >
        {type.label}
      </MenuItem>
    ))}
  </Select>
</FormControl>




          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Form Fields
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addField}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Add Field
            </Button>
          </Box>

          {formData.fields.map((field, index) => (
            <Paper
              key={index}
              elevation={2}
               sx={{
    p: 2,
    mb: 2,
     backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    
  }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Field {index + 1}
                </Typography>
                {formData.fields.length > 1 && (
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeField(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
<TextField
  label="Field Label *"
  fullWidth
  value={field.label}
  onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
  required
  sx={{
    '& .MuiInputLabel-root': {
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // White in dark, black in light
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main, // Focused label color
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider, // Border color
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main, // Hover border color
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main, // Focused border color
      },
      '& .MuiOutlinedInput-input': {
        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Input text color
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)', // Helper text color
    },
  }}
/>
 <TextField
  label="Field Name *"
  fullWidth
  value={field.name}
  onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
  required
  helperText="Use lowercase with underscores (e.g., full_name)"
  sx={{
    '& .MuiInputLabel-root': {
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // White in dark, black in light
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main, // Focused label color
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider, // Border color
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main, // Hover border color
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main, // Focused border color
      },
      '& .MuiOutlinedInput-input': {
        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Input text color
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)', // Helper text color
    },
  }}
/>



              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl fullWidth required>
   <InputLabel
    shrink
    sx={{
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Label color
      '&.Mui-focused': {
        color: theme.palette.primary.main, // Focused label color
      },
    }}
  >
    Field Type *
  </InputLabel>

  <Select
    value={field.type}
    onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
    label="Field Type *"
    sx={{
      color: 'text.primary',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
      },
    }}
  >
    {fieldTypes.map((type) => (
      <MenuItem key={type.value} value={type.value}>
        {type.label}
      </MenuItem>
    ))}
  </Select>
</FormControl>
<FormControlLabel
  control={
 <Checkbox
  checked={field.required}
  onChange={(e) =>
    handleFieldChange(index, "required", e.target.checked)
  }
  sx={{
    color:
      theme.palette.mode === "dark"
        ? "#9ca3af"        // unchecked in dark
        : "#4b5563",      // unchecked in light
    "&.Mui-checked": {
      color:
        theme.palette.mode === "dark"
          ? "#60a5fa"     // checked in dark
          : "#2563eb",   // checked in light
    },
  }}
/>
  }
  label="Required Field"
  sx={{
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Label color
    '& .MuiTypography-root': {
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Text color
    },
  }}
/>

                
              </Box>
            </Paper>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={!isFormValid() || loading}
              sx={{ backgroundColor: theme.palette.primary.main }}
            >
              {loading ? 'SAVING...' : 'SAVE'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={loading}
            >
              CANCEL
            </Button>
          </Box>
        </form>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddFormDesign;



